import React, { useEffect, useState } from "react";
import Heading from "../../Components/Heading/Heading";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  useTheme,
} from "@mui/material";
import axios from "axios";
import DataTable from "../../Components/DataTable/DataTable";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  EyeFill,
  EyeSlashFill,
  LockFill,
  PencilSquare,
  TrashFill,
  UnlockFill,
} from "react-bootstrap-icons";
import toast from "react-hot-toast";
import { getEntityByProperty } from "../../utils/Filters";

function Users() {
  const [data, setData] = useState();
  const [selectedUser, setSelectedUser] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openLockDialog, setOpenLockDialog] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const role = useSelector((state) => state.auth.role.roleName);
  let requestUrl =
    role === "SuperAdmin"
      ? `${process.env.REACT_APP_API_URL}/user/`
      : `${process.env.REACT_APP_API_URL}/user/employees`;
  const [passwordVisibility, setPasswordVisibility] = useState({});

  const togglePasswordVisibility = (id) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  const fetchUsers = () => {
    axios
      .get(requestUrl, { withCredentials: true })
      .then((res) => {
        setData(res.data);
      })
      .catch((error) => {
        toast.error(
          error?.message ||
            error?.response?.data?.message ||
            "فشل في تحميل المستخدمين"
        );
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [role, requestUrl]);

  const columns = [
    { field: "id", headerName: "رمز التعريف", width: 90 },
    { field: "name", headerName: "الإسم", width: 200 },
    { field: "phoneNumber", headerName: "رقم الهاتف", width: 200 },
    { field: "username", headerName: "إسم المستخدم", width: 150 },
    {
      field: "password",
      headerName: "كلمة السر",
      width: 150,
      renderCell: (params) => {
        const isPasswordVisible = passwordVisibility[params.row.id] || false;
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: "8px" }}>
              {isPasswordVisible ? params.row.password : "••••••••"}
            </span>
            <IconButton
              onClick={() => togglePasswordVisibility(params.row.id)}
              size="small"
              aria-label="toggle password visibility"
            >
              {isPasswordVisible ? <EyeSlashFill /> : <EyeFill />}
            </IconButton>
          </div>
        );
      },
    },
    {
      field: "WorkTime",
      headerName: "مدة العمل",
      width: 150,
      renderCell: (params) =>
        params.row.WorkTime ? params.row.WorkTime.duration : "",
    },
    {
      field: "Role",
      headerName: "نوع الوظيفة",
      width: 150,
      renderCell: (params) => (params.row.Role ? params.row.Role.name : ""),
    },
    {
      field: "Sites",
      headerName: "مواقع العمل",
      width: 200,
      renderCell: (params) => {
        const sitesNames = params.value.map((site) => site.name);
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              minHeight: "50px",
              height: "auto",
              alignItems: "center",
              padding: "12px 0",
            }}
          >
            {sitesNames.map((value) => (
              <Chip key={value} label={value} />
            ))}
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "space-evenly",
              alignItems: "center",
            }}
          >
            {role === "SuperAdmin" || role === "Admin" ? (
              <Button
                variant="contained"
                sx={{
                  width: "36px",
                  height: "36px",
                  padding: 0,
                  margin: 0,
                  minWidth: "auto",
                  backgroundColor: theme.palette.neutral[50],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral[100],
                  },
                }}
                onClick={() => handleLockClick(params.row.id)}
                disableElevation
              >
                {params.row?.isActivated ? (
                  <LockFill size={16} color={theme.palette.primary[600]} />
                ) : (
                  <UnlockFill size={16} color={theme.palette.primary[600]} />
                )}
              </Button>
            ) : (
              ""
            )}
            {role === "SuperAdmin" || role === "Admin" ? (
              <Button
                variant="contained"
                sx={{
                  width: "36px",
                  height: "36px",
                  padding: 0,
                  margin: 0,
                  minWidth: "auto",
                  backgroundColor: theme.palette.neutral[50],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral[100],
                  },
                }}
                onClick={() => handleEditClick(params.row)}
                disableElevation
              >
                <PencilSquare size={16} color={theme.palette.primary[600]} />
              </Button>
            ) : (
              ""
            )}
            {role === "SuperAdmin" ? (
              <Button
                variant="contained"
                sx={{
                  width: "36px",
                  height: "36px",
                  padding: 0,
                  margin: 0,
                  minWidth: "auto",
                  backgroundColor: theme.palette.neutral[50],
                  "&:hover": {
                    backgroundColor: theme.palette.neutral[100],
                  },
                }}
                onClick={() => handleDeleteClick(params.row.id)}
                disableElevation
              >
                <TrashFill size={16} color={theme.palette.primary[600]} />
              </Button>
            ) : (
              ""
            )}
          </Box>
        );
      },
    },
  ];

  const handleEditClick = (data) => {
    navigate(`/app/users/edit/${data.id}`, { state: data });
  };

  const handleDeleteClick = (id) => {
    const user = data.find((u) => u.id === id);
    setSelectedUser(user); // Set the id of the record to be deleted
    setOpenDeleteDialog(true); // Open the dialog
  };
  const handleLockClick = (id) => {
    const user = data.find((u) => u.id === id);
    setOpenLockDialog(true); // Open the dialog
    setSelectedUser(user);
  };

  const handleLockUser = async () => {
    if (selectedUser.id) {
      try {
        const user = getEntityByProperty("id", data, selectedUser.id);
        if (!user) {
          return;
        }
        const res = await axios.put(
          `${process.env.REACT_APP_API_URL}/user/${selectedUser.id}`,
          { isActivated: !user.isActivated },
          { withCredentials: true }
        );
        if (res) {
          toast.success("تم تعديل وضع حساب المستخدم بنجاح!");
          fetchUsers();
        }
      } catch (error) {
        console.log(error);
      }
    }
    setOpenLockDialog(false);
  };
  const handleDelete = async () => {
    if (selectedUser.id) {
      try {
        const res = await axios.delete(
          `${process.env.REACT_APP_API_URL}/user/${selectedUser.id}`,
          {
            withCredentials: true,
          }
        );
        if (res) {
          toast.success("تم حذف المستخدم بنجاح!");
          setData((prevData) =>
            prevData.filter((item) => item.id !== selectedUser.id)
          );
          setSelectedUser(null);
        }
      } catch (error) {
        toast.error(error.message);
        setSelectedUser(null);
      }
    }
    setOpenDeleteDialog(false);
  };

  const onButtonClick = (url) => {
    navigate(url);
  };

  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);
  const handleCloseLockDialog = () => setOpenLockDialog(false);

  return (
    <div>
      <Heading
        title="المستخدمين"
        button="إضافة مستخدم جديد"
        onButtonClick={() => onButtonClick("/app/users/new")}
      />
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"تأكيد الحذف"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            هل أنت متأكد من أنك تريد حذف هذا الموظّف؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} autoFocus color={"error"}>
            إغلاق
          </Button>
          <Button onClick={handleDelete} variant="contained" color={"error"}>
            حذف
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openLockDialog}
        onClose={handleCloseLockDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"تأكيد قفل حساب المستخدم"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {selectedUser?.isActivated
              ? "هل أنت متأكد من أنك تريد إلغاء تفعيل حساب هذا المستخدم؟"
              : "هل أنت متأكد من أنك تريد إعادة تفعيل حساب هذا المستخدم؟"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLockDialog} autoFocus color={"warning"}>
            إغلاق
          </Button>
          <Button
            onClick={handleLockUser}
            variant="contained"
            color={"warning"}
          >
            موافق
          </Button>
        </DialogActions>
      </Dialog>
      <DataTable data={data} dataColumns={columns} />
    </div>
  );
}

export default Users;
